import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, money } from "@/lib/api";
import { Truck, Lock, ArrowLeft, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, validateCart, stockIssues, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: user?.phone || "",
  });
  useEffect(() => {
    validateCart().then((r) => { if (!r.ok) toast.error("Some items in your cart are no longer available in the requested quantity."); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const hasIssues = Object.keys(stockIssues).length > 0;
  const HST_RATE = 0.13;
  const hst = subtotal * HST_RATE;
  const total = subtotal + hst;

  const placeOrder = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email so we can send your invoice.");
      return;
    }
    const check = await validateCart();
    if (!check.ok) { toast.error("Stock changed — please review your cart."); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/orders/public", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        customer_name: form.name.trim(),
        customer_email: form.email.trim(),
        customer_phone: form.phone.trim(),
        origin_url: window.location.origin,
      });
      clearCart();
      navigate(`/pay/${r.data.pay_token}`);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not start your order. Please try again.");
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <main className="max-w-4xl mx-auto px-4 py-16 text-center">
        <div className="font-heading text-2xl">Your cart is empty</div>
        <Link to="/" className="mt-4 inline-flex bg-brand-navy text-white rounded-full px-5 h-10 items-center font-bold">Browse deals</Link>
      </main>
    );
  }

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="checkout-page">
      <button onClick={() => navigate("/cart")} className="text-sm text-slate-600 hover:text-brand-red inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="h-4 w-4" /> Back to cart
      </button>
      <h1 className="font-heading text-3xl sm:text-4xl text-brand-navy mb-6">Checkout</h1>
      {hasIssues && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 text-sm font-bold text-brand-red flex items-center gap-2" data-testid="checkout-stock-warning">
          <AlertTriangle className="h-4 w-4" /> Stock changed: {Object.values(stockIssues).join(" · ")} — <Link to="/cart" className="underline">review your cart</Link>.
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_380px] gap-8">
        <div className="space-y-6">
          <Section title="Contact Information">
            <div className="grid sm:grid-cols-2 gap-3">
              <Input label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="checkout-name" required />
              <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="checkout-email" required />
              <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="checkout-phone" />
            </div>
            <p className="text-xs text-slate-500 mt-2">We'll email your secure invoice link to this address after payment.</p>
          </Section>

          <Section title="Pickup Method">
            <label className="flex items-start gap-3 rounded-xl border border-brand-green bg-brand-greenLight p-4 cursor-pointer">
              <input type="radio" name="pickup" checked readOnly className="mt-1" data-testid="pickup-drive-through" />
              <div>
                <div className="font-heading text-brand-greenDark flex items-center gap-2"><Truck className="h-4 w-4" /> Drive-Through Pickup</div>
                <div className="text-sm text-slate-700">Pay online now, then drive to our Barrhaven pickup lane to collect your order.</div>
              </div>
            </label>
          </Section>

          <Section title="Payment">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex items-start gap-3">
              <Lock className="h-5 w-5 text-brand-green mt-0.5" />
              <div className="text-sm text-slate-700">
                You'll be taken to a secure page to pay by <b>credit/debit card</b> or <b>Interac e-Transfer</b>.
                Your items are held for 24 hours while you complete payment.
              </div>
            </div>
          </Section>

          <button
            data-testid="checkout-place-order-btn"
            disabled={submitting || hasIssues}
            onClick={placeOrder}
            className="w-full inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-redHover text-white font-black uppercase tracking-wider h-12 rounded-full active:scale-95 transition disabled:opacity-60 disabled:cursor-not-allowed">
            {submitting ? <><Loader2 className="h-5 w-5 animate-spin" /> Starting secure checkout…</> : "Continue to secure payment"}
          </button>
        </div>

        <aside className="bg-white border border-slate-200 rounded-2xl p-6 h-fit sticky top-24">
          <div className="font-heading text-xl text-brand-navy mb-4">Order Summary</div>
          <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {items.map((it) => (
              <div key={it.id} className="flex gap-3">
                <img src={it.featured_image || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='56'%3E%3Crect width='100%25' height='100%25' fill='%23f1f5f9'/%3E%3C/svg%3E"} alt="" className="h-14 w-14 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold line-clamp-1">{it.product_name}</div>
                  <div className="text-xs text-slate-500">Qty {it.quantity} · {it.condition}</div>
                </div>
                <div className="text-sm font-bold">{money(it.price * it.quantity)}</div>
              </div>
            ))}
          </div>
          <div className="border-t mt-4 pt-4 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-bold">{money(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">HST (13%)</span><span className="font-bold">{money(hst)}</span></div>
          </div>
          <div className="border-t mt-3 pt-3 flex justify-between items-baseline">
            <span className="font-heading text-brand-navy">Total</span>
            <span className="font-heading text-xl text-brand-navy" data-testid="checkout-total">{money(total)}</span>
          </div>
        </aside>
      </div>
    </main>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-6">
    <h2 className="font-heading text-lg text-brand-navy mb-4">{title}</h2>
    {children}
  </div>
);

const Input = ({ label, value, onChange, type = "text", required, testId }) => (
  <label className="block">
    <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{label}{required && " *"}</span>
    <input type={type} value={value} required={required} data-testid={testId}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full h-11 rounded-lg border border-slate-200 px-3 focus:outline-none focus:ring-2 focus:ring-brand-navy" />
  </label>
);
